import React from 'react';
import { usePortal } from '../../contexts/PortalContext';
import { HOME_ORDER, NEW_PROJECT, SETTINGS, TILE_BY_KEY, type HomeTile } from '../../utils/homeTiles';
import { PANEL_EDGE, PANEL_GROUND } from '../../utils/studioSurfaces';
import type { StudioView } from '../../utils/studioViewHistory';

/**
 * The ten squares that are the body of the studio front page.
 *
 * Built 4 Nov 2026. The frames, the grading, the running order and the
 * reason the two drawn squares sit apart are all in utils/homeTiles.ts —
 * read that first. The reference Tom gave for the drawing is the equipment
 * grid on Portal LA's catalogue site: frame, label, one line under it,
 * arrow.
 *
 * **Four things here are deliberate and easy to undo by accident.**
 *
 * 1. **The scrim, not a plate.** The labels were boxes of paper at first and
 *    Tom was right that white type on the frame is cleaner. It only works
 *    because every tile carries a gradient fading up from its bottom edge:
 *    three of the eight photographs are near-white, and without the scrim
 *    the label on those is unreadable. Remove the scrim and you lose the
 *    pale tiles, not just some polish.
 * 2. **The grading is CSS over the original file.** Per-frame, from
 *    homeTiles.ts. Nothing is baked into the images, so swapping one is a
 *    URL change.
 * 3. **No stripe or grain overlay.** A CSS "film grain" was tried and showed
 *    as visible diagonal hatching on the pale frames. Standing instruction:
 *    never again, anywhere.
 * 4. **ONLY THE PICTURE MOVES (Tom, 5 Nov).** The label used to lift a few
 *    pixels on hover as well. Two things moving at two speeds inside one
 *    small square is fidget, not life. Type and arrows are fixed; what moves
 *    is whatever the square is a picture OF — the photograph, the arcs, the
 *    cross. Do not re-animate the type.
 *
 * **MATCH THE TRAVEL, NOT THE PERCENTAGE (Tom, 5 Nov: "not enough motion on
 * new projects button").** All three pictures were given the same ~7% hover
 * scale and only two of them read. A photograph fills its square, so 7%
 * moves its edges about thirteen pixels; the cross is a small mark in the
 * middle, so the same 7% moved its arm-ends four, which is invisible. The
 * cross is now 1.30, chosen so its endpoints travel the same thirteen
 * pixels as a photograph's edge. **The number that should match across these
 * tiles is the distance something moves on screen, not the scale factor.**
 *
 * The Settings arcs are the third case: they run off three edges, so they
 * drift sideways rather than scaling, and the motion reads as artwork
 * continuing past the frame.
 *
 * **`fill` — size to the space rather than to a shape (5 Nov).** The front
 * page must not scroll on a laptop, so the grid cannot insist on squares and
 * hope they fit: with `fill` it becomes two rows that divide whatever height
 * Home has left over. At the column widths this page uses the cells come out
 * close to square anyway. Below xl the squares come back and the page
 * scrolls.
 */

interface Props {
  isOwner: boolean;
  onSelectView: (view: StudioView) => void;
  onNewProject: () => void;
  onOpenSettings: () => void;
  /** Divide the available height instead of drawing squares. */
  fill?: boolean;
}
const SCRIM = 'linear-gradient(to top, rgba(0,0,0,0.92) 0%, rgba(0,0,0,0.72) 18%, rgba(0,0,0,0.3) 40%, rgba(0,0,0,0) 62%)';

/* Real middle dots, not HTML entities: JSX does not decode entities inside a
   string attribute, so `sub="a &middot; b"` would print the ampersand. */
const SETTINGS_SUB = 'Team · account · workers';
const NEW_PROJECT_SUB = 'Start something';

/** The line weight both drawn tiles share with the brand's own artwork. */
const LINE = '#939598';

/**
 * **THE SETTINGS ARCS, AND WHY THEY ARE DRAWN THIS WAY (5 Nov 2026).**
 *
 * This is the brand's Circular Line Art (guidelines p31): a family of
 * equally offset circles, read as a sweep of evenly spaced arcs.
 *
 * The first version put ten circles of r=172 in a 320×360 box on a tile
 * that was then two columns wide. When Settings dropped to one column the
 * same artwork, sliced into a narrow square, showed only the dense middle
 * where every arc crosses — Tom: *"fix line art on settings"*, and he was
 * right, it had turned into a blob with a bite out of it.
 *
 * Two things fix it and both matter:
 *
 *   • **The radius must exceed half the viewBox height** (240 against 320),
 *     or the circles close inside the square and you see lens shapes rather
 *     than a sweep. At r=240 with cy=160 every arc leaves through the top
 *     and bottom edges, which is what makes it read as artwork continuing
 *     past the frame.
 *   • **The element is the full width of the tile**, so a square viewBox in
 *     a square tile scales 1:1 and nothing is cropped into. The old version
 *     was 78% wide with `slice`, which magnified the art and cropped the
 *     left — straight into the crossing zone.
 *
 * Nine circles at 18px spacing, not ten at 15: at this scale the tighter
 * set moirés against the pixel grid.
 *
 * **THE 12px OVERHANG IS NOT DECORATION.** `xMaxYMid` pins the drawing to
 * the right edge, so the hover drift used to pull that edge inward and open
 * a bare band down the side of the tile (Tom: "during motion on settings
 * the line art runs out"). The element is 12px wider than the tile and
 * offset 12px right, clipped at rest, so the drift spends exactly that
 * overhang. The translate, the width and the offset are the same number and
 * must move together.
 */
const ARC_CX = [240, 258, 276, 294, 312, 330, 348, 366, 384];
const ARC_R = 240;

/** Static — see note 4. It marks the tile as a way through, nothing more. */
function Arrow() {
  return <svg viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4 shrink-0 text-white/55" data-id="element-1031" data-mp="25etlr0ks795p">
      <path d="M4 12h15M13 6l6 6-6 6" data-id="element-1032" data-mp="1lrgl3k8l6yrd" />
    </svg>;
}
function Caption({
  label,
  sub
}: {
  label: string;
  sub: string;
}) {
  return <span className="absolute bottom-3 left-4 right-4 flex items-end justify-between gap-3" data-id="element-1033" data-mp="1qr2zhx05il1r">
      <span className="min-w-0" data-id="element-1034" data-mp="1crk321gpcy5a">
        <span className="block text-[15px] uppercase tracking-wide2 text-white" data-id="element-1035" data-mp="1cslg2bha3upn">{label}</span>
        {sub && <span className="mt-1 block truncate text-[13px] text-white/60" data-id="element-1036" data-mp="1gu6pfmultqv3">{sub}</span>}
      </span>
      <Arrow data-id="element-1037" data-mp="24jrjp1w7kajo" />
    </span>;
}
function Face({
  tile,
  sub
}: {
  tile: HomeTile;
  sub: string;
}) {
  return <>
      <span className="absolute inset-0 scale-[1.01] transition-transform duration-[900ms] ease-out group-hover:scale-[1.07] motion-reduce:transition-none motion-reduce:group-hover:scale-[1.01]" style={{
      backgroundImage: `url(${tile.image})`,
      backgroundSize: tile.size ?? 'cover',
      backgroundPosition: 'center',
      filter: tile.filter
    }} data-id="element-1038" data-mp="29im4g9dqk96f" />
      <span className="absolute inset-0" style={{
      backgroundImage: SCRIM
    }} data-id="element-1039" data-mp="gsa8zi3xsdny" />
      <Caption label={tile.label} sub={sub} data-id="element-1040" data-mp="gq7986czf661" />
    </>;
}
export function HomeTiles({
  isOwner,
  onSelectView,
  onNewProject,
  onOpenSettings,
  fill = false
}: Props) {
  const {
    activeProjects
  } = usePortal();

  /* The one count that is already in context and therefore honest. Every
     other sub-line is a static description until its fetch exists — see
     homeTiles.ts. Never invent a number to fill the slot. */
  const count = activeProjects.length;
  const subFor = (tile: HomeTile) => tile.key === 'clients' ? `${count} active project${count === 1 ? '' : 's'}` : tile.sub;
  const gridClass = `grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-5 xl:gap-4 ${fill ? 'xl:h-full xl:grid-rows-2' : ''}`;
  const cellClass = `group relative block aspect-square overflow-hidden bg-ink ${fill ? 'xl:aspect-auto xl:h-full' : ''}`;
  /* The two drawn squares. Ground and edge from utils/studioSurfaces.ts,
     shared so they stay one family — both were drawn too light at first and
     both read as stuck on the page. */
  const drawnClass = `group relative block aspect-square overflow-hidden border text-left ${fill ? 'xl:aspect-auto xl:h-full' : ''}`;
  return <nav aria-label="Portal OS sections" className={gridClass} data-id="element-1041" data-mp="3hmht3c55cpw1">
      {HOME_ORDER.map(key => {
      /* NEW PROJECT. Tom, 5 Nov: *"the + NEW Project is an exciting thing
         to build — any way to make it more special? Maybe it is a tab
         rather than being stuck up there?"* It was a 10px button in the
         corner of the header; it is the only thing on this page that MAKES
         something, and it now has a square of its own.
          The logo was considered and rejected: the wordmark is already in
         the header, a logo on a button face reads as "home" or "about us",
         and a new project is an EMPTY thing while the logo is the most
         finished object Portal owns.
          The first drawing added camera-style corner brackets around the
         cross and Tom's note was *"this needs cleaning"* — brackets plus a
         cross reads as a focus reticle, which is a picture of FRAMING a
         shot, not of starting a job. One clean cross says it without the
         decoration, and it also stops the square competing with the
         Settings arcs for busyness.
          The 1.30 hover scale is not arbitrary — see the note on matching
         travel rather than percentage at the top of this file. */
      if (key === NEW_PROJECT) {
        return <button key={key} type="button" onClick={onNewProject} style={{
          backgroundColor: PANEL_GROUND,
          borderColor: PANEL_EDGE
        }} className={drawnClass} data-id="element-1042" data-mp="2cgi2n7tgh2nz">
              <svg viewBox="0 0 320 320" preserveAspectRatio="xMidYMid meet" aria-hidden="true" className="absolute inset-0 h-full w-full transition-transform duration-[900ms] ease-out group-hover:scale-[1.3] motion-reduce:transition-none motion-reduce:group-hover:scale-100" data-id="element-1043" data-mp="1101kh6qio7xf">
                <g fill="none" stroke={LINE} strokeWidth="1.1" strokeOpacity="0.6" data-id="element-1044" data-mp="me36i6h97g0t">
                  <path d="M160,96 L160,224" data-id="element-1045" data-mp="hlt7m0y5v5me" />
                  <path d="M96,160 L224,160" data-id="element-1046" data-mp="hl0brv0qq0j1" />
                </g>
              </svg>
              <Caption label="New project" sub={NEW_PROJECT_SUB} data-id="element-1047" data-mp="vc5226i7xggp" />
            </button>;
      }

      /* SETTINGS. One column, not two (Tom, 5 Nov) — it is the square you
         open monthly rather than daily. Read the note on ARC_CX above
         before changing the drawing: the radius, the element width and the
         overhang each fix a specific thing that went wrong. */
      if (key === SETTINGS) {
        return <button key={key} type="button" onClick={onOpenSettings} style={{
          backgroundColor: PANEL_GROUND,
          borderColor: PANEL_EDGE
        }} className={drawnClass} data-id="element-1048" data-mp="324fmt1hq46b3">
              <svg viewBox="0 0 320 320" preserveAspectRatio="xMaxYMid slice" aria-hidden="true" className="absolute -right-3 top-0 h-full w-[calc(100%_+_0.75rem)] transition-transform duration-[900ms] ease-out group-hover:-translate-x-3 motion-reduce:transition-none motion-reduce:group-hover:translate-x-0" data-id="element-1049" data-mp="1mkoqrcmk5y3c">
                <g fill="none" stroke={LINE} strokeWidth="1" strokeOpacity="0.45" data-id="element-1050" data-mp="13vi5bhudqsdu">
                  {ARC_CX.map(cx => <circle key={cx} cx={cx} cy={160} r={ARC_R} data-id="element-1051" data-mp="ozao8qe9q72s" />)}
                </g>
              </svg>
              <Caption label="Settings" sub={SETTINGS_SUB} data-id="element-1052" data-mp="2jbh7mchbfu66" />
            </button>;
      }
      const tile = TILE_BY_KEY[key];
      if (!tile) return null;
      if (tile.ownerOnly && !isOwner) return null;
      return tile.href ? <a key={tile.key} href={tile.href} target="_blank" rel="noreferrer" className={cellClass} data-id="element-1053" data-mp="2mu7r4h7howvo">
            <Face tile={tile} sub={subFor(tile)} data-id="element-1054" data-mp="2cugt45jpzzqu" />
          </a> : <button key={tile.key} type="button" onClick={() => tile.view && onSelectView(tile.view)} className={`${cellClass} text-left`} data-id="element-1055" data-mp="28twvl0v8iu46">
            <Face tile={tile} sub={subFor(tile)} data-id="element-1056" data-mp="30l30rvptbi0b" />
          </button>;
    })}
    </nav>;
}